import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { Button, Typography, useTheme, Tabs, Tab } from "@mui/material";
import { useSwipeable } from "react-swipeable";
import SwipeIcon from "@mui/icons-material/Swipe";
import "./RecipeDetail.css";
import QuickActions from "./RecipeQuickActions";
import { FavoritesContext } from "../contexts/FavoritesContext";
import RecipeCard, { getAccentColors } from "./RecipeCard";
import RecipeNotes from "./RecipeNotes";

const DESIRED_SPEED = 200; // em/s

function RecipeDetail({
  recipe,
  index,
  onRecipeClick,
  disableExpansion,
  expanded,
  isSelected,
  isHinting,
  onHintComplete,
}) {
  const { favoritedRecipes } = useContext(FavoritesContext);
  const isFavorited = favoritedRecipes.includes(recipe.id);
  const contentRef = useRef(null);
  const [isOpen, setIsOpen] = useState(expanded || false);
  const [contentHeight, setContentHeight] = useState(0);
  const [transitionDuration, setTransitionDuration] = useState("0.3s");
  const [swipeIconAnimating, setSwipeIconAnimating] = useState(false);
  const [cardMovingOver, setCardMovingOver] = useState(false);
  const [showHintOverlay, setShowHintOverlay] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const isAnimatingRef = useRef(false);

  const theme = useTheme();

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const descriptionRef = useRef(null);
  
  // Preview panel description truncation
  const [showFullPreviewDescription, setShowFullPreviewDescription] = useState(false);
  const [isPreviewDescriptionTruncated, setIsPreviewDescriptionTruncated] = useState(false);
  const previewDescriptionRef = useRef(null);

  // Reset preview description state when recipe changes
  useEffect(() => {
    setShowFullPreviewDescription(false);
  }, [recipe.id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Hint sequence effect
  useEffect(() => {
    let cardMoveTimeout;
    let showActionsTimeout;
    let resetHintTimeout;
    let overlayTimeout;

    if (isHinting) {
      const SWIPE_ICON_DURATION = 1000; // Match CSS animation duration
      const CARD_MOVE_DURATION = 250;
      const ACTIONS_DISPLAY_DURATION = 2000;
      const TOTAL_HINT_DURATION =
        SWIPE_ICON_DURATION + CARD_MOVE_DURATION + ACTIONS_DISPLAY_DURATION;

      // Start the swipe icon animation and show the overlay
      setShowHintOverlay(true);
      setSwipeIconAnimating(true);

      // Move the card over after the swipe icon animation ends
      cardMoveTimeout = setTimeout(() => {
        setCardMovingOver(true);
      }, SWIPE_ICON_DURATION);

      // Hide the overlay after the swipe icon animation ends
      overlayTimeout = setTimeout(() => {
        setShowHintOverlay(false);
      }, SWIPE_ICON_DURATION);

      // Show the quick actions after the card has moved
      showActionsTimeout = setTimeout(() => {
        setShowActions(true);
      }, SWIPE_ICON_DURATION + CARD_MOVE_DURATION);

      // Reset the hint after displaying actions
      resetHintTimeout = setTimeout(() => {
        setSwipeIconAnimating(false);
        setCardMovingOver(false);
        setShowActions(false);

        // Inform parent component that hint sequence is complete
        if (onHintComplete) {
          onHintComplete();
        }
      }, TOTAL_HINT_DURATION);

      return () => {
        clearTimeout(cardMoveTimeout);
        clearTimeout(showActionsTimeout);
        clearTimeout(resetHintTimeout);
        clearTimeout(overlayTimeout);
      };
    }
  }, [isHinting, onHintComplete]);

  const updateContentHeight = useCallback((skipDuringAnimation = false) => {
    if (skipDuringAnimation && isAnimatingRef.current) return;
    
    if (contentRef.current) {
      requestAnimationFrame(() => {
        if (!contentRef.current) return;
        const heightInPx = contentRef.current.scrollHeight;
        const heightInEm = heightInPx / 16;
        const durationInSeconds = heightInEm / DESIRED_SPEED;
        
        // Batch state updates
        setContentHeight(heightInPx);
        setTransitionDuration(`${durationInSeconds}s`);
      });
    }
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        // Skip updates during animation to prevent jitter
        updateContentHeight(true);
      });
      resizeObserver.observe(contentRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [contentRef, updateContentHeight]);

  useLayoutEffect(() => {
    updateContentHeight();
  }, [recipe, isOpen, showAllIngredients, updateContentHeight]);

  // Handle tab changes and description expansion with multiple delayed checks
  // to catch late-rendering content like DataGrid
  useEffect(() => {
    const timers = [
      setTimeout(() => updateContentHeight(), 50),
      setTimeout(() => updateContentHeight(), 150),
      setTimeout(() => updateContentHeight(), 300),
    ];
    return () => timers.forEach(t => clearTimeout(t));
  }, [activeTab, showFullDescription, showFullPreviewDescription, updateContentHeight]);
  
  // Additional height check after card opens to catch async content
  useEffect(() => {
    if (isOpen) {
      const timers = [
        setTimeout(() => updateContentHeight(), 100),
        setTimeout(() => updateContentHeight(), 250),
        setTimeout(() => updateContentHeight(), 500),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [isOpen, updateContentHeight]);

  useEffect(() => {
    window.addEventListener("resize", updateContentHeight);
    return () => {
      window.removeEventListener("resize", updateContentHeight);
    };
  }, [updateContentHeight]);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setShowActions(true);
      setSwipeIconAnimating(false);
      setCardMovingOver(false);
    },
    onSwipedRight: () => {
      setShowActions(false);
      setSwipeIconAnimating(false);
      setCardMovingOver(false);
    },
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false,
    delta: 10,
  });

  const handleTap = (e) => {
    if (e.target.closest(".quick-action-button")) {
      return;
    }
    if (!showActions) {
      if (disableExpansion) {
        if (onRecipeClick) {
          onRecipeClick(recipe);
        }
      } else {
        // Mark animation start
        isAnimatingRef.current = true;
        setIsOpen(!isOpen);
        
        // Clear animation flag after transition completes and do final height check
        const duration = parseFloat(transitionDuration) * 1000 || 300;
        setTimeout(() => {
          isAnimatingRef.current = false;
          // Final height check after animation
          updateContentHeight();
        }, duration + 50);
      }
    } else {
      setShowActions(false);
    }

    // Cancel the hint sequence
    setSwipeIconAnimating(false);
    setCardMovingOver(false);
  };


  // Helper function to sanitize values
  function sanitize(value) {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "string" && value.toLowerCase() === "null") {
      return "";
    }
    return value;
  }

  const rows = recipe.ingredients.map((ingredient, idx) => ({
    id: idx,
    name: sanitize(ingredient.name).toLowerCase(),
    preparation: sanitize(ingredient.preparation).toLowerCase(),
    quantity: `${sanitize(ingredient.quantity?.value)} ${sanitize(
      ingredient.quantity?.unit
    )}`.trim(),
  }));

  const hasMoreThanSixIngredients = rows.length > 6;

  const MAX_LINES = 5;
  const MAX_PREVIEW_LINES = 2;

  // Helper function to get line height
  function getLineHeight(element) {
    const computedStyle = window.getComputedStyle(element);
    let lineHeight = computedStyle.lineHeight;

    if (lineHeight === "normal") {
      const fontSize = parseFloat(computedStyle.fontSize);
      lineHeight = fontSize * 1.2;
    } else {
      lineHeight = parseFloat(lineHeight);
    }

    return lineHeight;
  }

  // Check if description needs truncation
  useLayoutEffect(() => {
    if (descriptionRef.current && isOpen) {
      const descriptionElement = descriptionRef.current;
      const lineHeight = getLineHeight(descriptionElement);
      const maxHeight = lineHeight * MAX_LINES;

      if (descriptionElement.scrollHeight > maxHeight) {
        setIsDescriptionTruncated(true);
      } else {
        setIsDescriptionTruncated(false);
      }
    } else {
      setIsDescriptionTruncated(false);
    }
  }, [recipe.description, showFullDescription, isOpen, MAX_LINES]);

  // Check if preview panel description needs truncation
  // Note: using expanded && disableExpansion directly since isPreviewPanel is defined later
  useLayoutEffect(() => {
    const isPanelMode = expanded && disableExpansion;
    if (previewDescriptionRef.current && isPanelMode && !showFullPreviewDescription) {
      const el = previewDescriptionRef.current;
      // Check if content is actually being clamped by comparing scrollHeight to clientHeight
      // Adding a small buffer (2px) to account for rounding
      const isClamped = el.scrollHeight > el.clientHeight + 2;
      setIsPreviewDescriptionTruncated(isClamped);
    } else if (!isPanelMode) {
      setIsPreviewDescriptionTruncated(false);
    }
  }, [recipe.description, showFullPreviewDescription, expanded, disableExpansion]);

  const handleViewOriginalRecipe = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (recipe.path.startsWith("http")) {
      window.open(recipe.path, "_blank");
    } else {
      const encodedBookName = encodeURIComponent(recipe.books.name);
      const pathParts = recipe.path.split("/");
      const limitedPath = pathParts.slice(-2).join("/");
      const encodedPath = encodeURIComponent(limitedPath);
      const encodedRecipeTitle = encodeURIComponent(recipe.title);
      const finalURL = `/books/${encodedBookName}?spineLocation=${encodedPath}&query=${encodedRecipeTitle}`;
      window.open(finalURL, "_blank", "noopener,noreferrer");
    }
  };

  const isDark = theme.palette.mode === "dark";
  const accent = getAccentColors(isDark);

  // Check if this is a preview panel (expanded mode)
  const isPreviewPanel = expanded && disableExpansion;
  const cellPadding = isPreviewPanel ? "12px 16px" : "14px 20px";

  const columns = [
    {
      field: "name",
      headerName: "ingredient",
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            fontWeight: 400,
          }}
        >
          <div>{params.row.name}</div>
          {params.row.preparation && (
            <div
              style={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
                marginTop: "0.25em",
                fontSize: isPreviewPanel ? "0.7rem" : "0.75rem",
                opacity: 0.7,
              }}
            >
              {params.row.preparation}
            </div>
          )}
        </div>
      ),
    },
    {
      field: "quantity",
      headerName: "quantity",
      flex: 0.8,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 500,
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  // Quick Actions component
  const quickActionsComponent = (
    <QuickActions
      recipe={recipe}
      isFavorited={isFavorited}
      showActions={showActions}
    />
  );

  // Hint Overlay
  const hintOverlayComponent = showHintOverlay && (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDark ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.5)",
        backdropFilter: "blur(2px)",
        zIndex: 1,
        pointerEvents: "none",
        borderRadius: "16px",
      }}
    />
  );

  // Swipe Icon
  const swipeIconComponent = swipeIconAnimating && (
    <div
      style={{
        position: "relative",
        right: "10px",
        display: "flex",
        alignItems: "center",
        zIndex: 2,
      }}
    >
      <SwipeIcon
        className={swipeIconAnimating ? "swipe-icon-animate" : ""}
        style={{
          position: "absolute",
          bottom: "calc(100% + 12px)",
          left: "90%",
          transform: "translate(-50%, -100%)",
          color: theme.palette.primary.primary,
        }}
      />
    </div>
  );

  // Ingredients content (shared between layouts)
  const ingredientsContent = (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "relative",
      }}
    >
      <DataGrid
        rows={
          // Show all ingredients in preview panel on desktop, but truncate on mobile
          showAllIngredients || !hasMoreThanSixIngredients || (isPreviewPanel && !isMobile)
            ? rows
            : rows.slice(0, 6)
        }
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu={true}
        getRowHeight={() => "auto"}
        autoHeight
        sx={{
          borderRadius: isPreviewPanel ? "16px" : "18px",
          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
          fontSize: isPreviewPanel ? "12px" : "12px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
          background: isDark
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.01)",
          overflow: "hidden",
          "& .MuiDataGrid-columnHeader": {
            color: theme.palette.text.primary,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)",
            minHeight: isPreviewPanel ? "44px" : "48px",
            maxHeight: isPreviewPanel ? "44px" : "48px",
            padding: cellPadding,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "600",
            fontSize: isPreviewPanel ? "12px" : "12px",
            letterSpacing: "0.02em",
            textTransform: "lowercase",
          },
          "& .MuiDataGrid-columnHeaders": {
            minHeight: isPreviewPanel ? "44px !important" : "48px !important",
            maxHeight: isPreviewPanel ? "44px !important" : "48px !important",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: 0,
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-row": {
            transition: "background-color 0.15s ease",
            "&:nth-of-type(even)": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.01)",
            },
            "&:nth-of-type(odd)": {
              backgroundColor: "transparent",
            },
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(187, 134, 252, 0.08)"
                : "rgba(99, 102, 241, 0.06)",
              "& .MuiDataGrid-cell": {
                color: theme.palette.text.primary,
              },
            },
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            lineHeight: "1.4em",
            padding: cellPadding,
            boxSizing: "border-box",
            borderColor: isDark
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.03)",
          },
          "& .MuiDataGrid-cell[data-field='quantity']": {
            justifyContent: "flex-end",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight:
              showAllIngredients || !hasMoreThanSixIngredients || (isPreviewPanel && !isMobile)
                ? 0
                : "44px",
            border: "none",
          },
          "& .MuiTablePagination-root": {
            display: "none",
          },
        }}
      />
      {hasMoreThanSixIngredients && !showAllIngredients && !(isPreviewPanel && !isMobile) && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "40px",
            background: isDark
              ? "rgba(20, 20, 25, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: accent.primary,
            fontSize: isPreviewPanel ? "12px" : "12px",
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            cursor: "pointer",
            fontWeight: "500",
            letterSpacing: "0.01em",
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
            borderBottomLeftRadius: isPreviewPanel ? "16px" : "18px",
            borderBottomRightRadius: isPreviewPanel ? "16px" : "18px",
            zIndex: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(187, 134, 252, 0.1)"
                : "rgba(99, 102, 241, 0.08)",
              color: isDark ? "#c9a4fc" : "#4f46e5",
            },
          }}
          onClick={() => setShowAllIngredients(true)}
        >
          show all ({rows.length - 6} more)
        </Box>
      )}
    </Box>
  );

  // Instructions content (shared between layouts)
  const instructionsContent = (
    <Box
      style={{
        "--primary-color": accent.primary,
        "--secondary-color": accent.secondary,
        "--text-color": theme.palette.text.primary,
        "--font-family": "'JetBrains Mono', 'IBM Plex Mono', monospace",
        "--bg-color": isDark
          ? "rgba(30, 30, 35, 1)"
          : "rgba(255, 255, 255, 1)",
      }}
      sx={{
        "& .customList": {
          paddingLeft: isPreviewPanel ? "1.25em" : "1.5em",
          marginTop: 0,
        },
        "& .customListItem": {
          marginBottom: isPreviewPanel ? "1em" : "1.25em",
          fontSize: isPreviewPanel ? "13px" : "14px",
          lineHeight: isPreviewPanel ? "1.7" : "1.8",
          fontWeight: 400,
          color: theme.palette.text.primary,
          "&::before": {
            fontWeight: "600",
            fontSize: isPreviewPanel ? "0.9em" : "1em",
          },
        },
      }}
    >
      <ol className="customList">
        {recipe.instructions.map((instruction, idx) => (
          <li key={idx} className="customListItem">
            {instruction}
          </li>
        ))}
      </ol>
    </Box>
  );

  // Notes content
  const notesContent = (
    <RecipeNotes recipeId={recipe.id} />
  );

  // Two-column tabbed layout for preview panel
  const previewPanelContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {/* Description above the panels */}
      {recipe.description && (
        <Typography
          ref={previewDescriptionRef}
          variant="body1"
          style={{
            marginLeft: "1em",
            marginRight: "1em",
            marginTop: "0.25em",
            marginBottom: "1.25em",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.85rem",
            lineHeight: 1.6,
            color: theme.palette.text.primary,
            opacity: 0.8,
            ...(!showFullPreviewDescription
              ? {
                  display: "-webkit-box",
                  WebkitLineClamp: MAX_PREVIEW_LINES,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  position: "relative",
                }
              : {}),
          }}
        >
          {recipe.description}
          {isPreviewDescriptionTruncated && !showFullPreviewDescription && (
            <span
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                // Card gradient is 135deg from rgba(30,30,35) to rgba(20,20,25)
                // At top-right area, color is approximately in the middle
                background: isDark
                  ? "linear-gradient(to right, transparent, rgba(26, 26, 31, 1) 40%)"
                  : "linear-gradient(to right, transparent, rgba(252, 253, 254, 1) 40%)",
                paddingLeft: "3em",
                fontWeight: "500",
                cursor: "pointer",
                color: accent.primary,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowFullPreviewDescription(true);
              }}
            >
              ...show more
            </span>
          )}
          {isPreviewDescriptionTruncated && showFullPreviewDescription && (
            <span
              style={{
                marginLeft: "8px",
                fontWeight: "500",
                cursor: "pointer",
                color: accent.primary,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowFullPreviewDescription(false);
              }}
            >
              ...show less
            </span>
          )}
        </Typography>
      )}

      {/* Two-column layout */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          padding: recipe.description ? "0 1em 1em" : "0.75em 1em 1em",
          position: "relative",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Left column - Image without card frame */}
      <Box
        sx={{
          width: "40%",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          alignSelf: "flex-start",
          height: "fit-content",
          pt: "0.5em",
          animation: "slideInLeft 0.3s ease-out",
          "@keyframes slideInLeft": {
            "0%": {
              opacity: 0,
              transform: "translateX(-8px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateX(0)",
            },
          },
        }}
      >
        {/* Image - no wrapper frame */}
        {recipe.image_url ? (
          <Box
            sx={{
              width: "100%",
              aspectRatio: "4/5",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: `0 4px 20px -4px rgba(0,0,0,${isDark ? "0.35" : "0.15"})`,
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                boxShadow: `0 8px 30px -4px rgba(0,0,0,${isDark ? "0.45" : "0.2"})`,
              },
            }}
          >
            <img
              alt={recipe.title}
              src={recipe.image_url}
              onLoad={updateContentHeight}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              aspectRatio: "4/5",
              borderRadius: "12px",
              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.7rem",
                fontFamily: "'JetBrains Mono', monospace",
                opacity: 0.4,
              }}
            >
              no image
            </Typography>
          </Box>
        )}
      </Box>

      {/* Right column - Tabbed content with max width */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          maxWidth: "600px", // Limit line length for readability
          overflow: "hidden",
          animation: "slideInRight 0.3s ease-out 0.1s both",
          "@keyframes slideInRight": {
            "0%": {
              opacity: 0,
              transform: "translateX(8px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateX(0)",
            },
          },
        }}
      >
        {/* Tabs - cleaner presentation */}
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            mt: "-2px", // Align with image top
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              e.stopPropagation();
              setActiveTab(newValue);
            }}
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": {
                backgroundColor: accent.primary,
                height: "2px",
              },
              "& .MuiTab-root": {
                minHeight: 0,
                padding: "8px 16px",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "lowercase",
                letterSpacing: "0.01em",
                color: theme.palette.text.secondary,
                transition: "color 0.2s ease",
                "&.Mui-selected": {
                  color: accent.primary,
                  fontWeight: 600,
                },
                "&:hover": {
                  color: theme.palette.text.primary,
                },
              },
            }}
          >
            <Tab label="ingredients" />
            <Tab label="instructions" />
            <Tab label="notes" />
          </Tabs>
        </Box>

        {/* Scrollable tab content - fills remaining space with crossfade */}
        <Box 
          sx={{ 
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            pt: 2,
            pr: 1,
            pb: 2,
            position: "relative",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
              borderRadius: "3px",
              "&:hover": {
                background: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
              },
            },
          }}
        >
          {/* Tab content with fade transition */}
          <Box
            key={activeTab}
            sx={{
              animation: "fadeIn 0.2s ease-out",
              "@keyframes fadeIn": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(4px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            {activeTab === 0 && ingredientsContent}
            {activeTab === 1 && instructionsContent}
            {activeTab === 2 && notesContent}
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );

  // Standard expanded content (for list cards) - now with tabs
  const standardExpandedContent = (
    <>
      {/* Description */}
      <Typography
        variant="body1"
        style={{
          marginBottom: "1em",
          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
          fontSize: "14px",
          lineHeight: "22px",
          color: theme.palette.text.primary,
          letterSpacing: "-0.01em",
          opacity: 0.9,
          ...(isDescriptionTruncated && !showFullDescription
            ? {
                display: "-webkit-box",
                WebkitLineClamp: MAX_LINES,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                position: "relative",
              }
            : {}),
        }}
        ref={descriptionRef}
      >
        {recipe.description ? recipe.description : ""}
        {isDescriptionTruncated && !showFullDescription && (
          <span
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              background: `linear-gradient(to right, transparent, ${isDark ? "#1e1e23" : "#fff"} 20%)`,
              paddingLeft: "16px",
              fontWeight: "500",
              cursor: "pointer",
              color: accent.primary,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowFullDescription(true);
            }}
          >
            ...show more
          </span>
        )}
      </Typography>

      {/* Image */}
      {recipe.image_url && (
        <Box
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            style={{
              maxWidth: "500px",
              height: "400px",
              borderRadius: "12px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"}`,
              overflow: "hidden",
              marginBottom: "1.25em",
              alignItems: "center",
              justifyContent: "center",
              alignSelf: "center",
              display: "flex",
              boxShadow: `0 4px 20px -4px rgba(0,0,0,${isDark ? "0.35" : "0.12"})`,
            }}
          >
            <img
              alt={`${recipe.title}`}
              src={recipe.image_url}
              onLoad={updateContentHeight}
              style={{
                objectFit: "cover",
                height: "100%",
                width: "100%",
                filter: isDark ? "brightness(0.9)" : "none",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            e.stopPropagation();
            setActiveTab(newValue);
          }}
          sx={{
            minHeight: 0,
            "& .MuiTabs-indicator": {
              backgroundColor: accent.primary,
              height: "2px",
            },
            "& .MuiTab-root": {
              minHeight: 0,
              padding: "10px 16px",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.8rem",
              fontWeight: 500,
              textTransform: "lowercase",
              letterSpacing: "0.01em",
              color: theme.palette.text.secondary,
              transition: "color 0.2s ease",
              "&.Mui-selected": {
                color: accent.primary,
                fontWeight: 600,
              },
              "&:hover": {
                color: theme.palette.text.primary,
              },
            },
          }}
        >
          <Tab label="ingredients" />
          <Tab label="instructions" />
          <Tab label="notes" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box
        key={activeTab}
        sx={{
          animation: "fadeIn 0.2s ease-out",
          "@keyframes fadeIn": {
            "0%": { opacity: 0, transform: "translateY(4px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {activeTab === 0 && (
          <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
            {ingredientsContent}
          </Box>
        )}
        {activeTab === 1 && instructionsContent}
        {activeTab === 2 && notesContent}
      </Box>
      
      {/* View original recipe button - mobile only, below tabs */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "1.5em 0",
            mt: 1,
          }}
        >
          <Button
            onClick={handleViewOriginalRecipe}
            variant="outlined"
            sx={{
              padding: "0.6em 1.75em",
              borderRadius: "12px",
              textAlign: "center",
              textDecoration: "none",
              fontSize: "0.8rem",
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontWeight: 500,
              letterSpacing: "0.01em",
              borderColor: accent.primary,
              color: accent.primary,
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(187, 134, 252, 0.1)"
                  : "rgba(99, 102, 241, 0.08)",
                borderColor: accent.primary,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 12px -2px ${accent.glow}`,
              },
            }}
          >
            view original recipe
          </Button>
        </Box>
      )}
    </>
  );

  // Expanded content wrapper
  const expandedContent = (
    <div
      ref={contentRef}
      style={{
        overflow: disableExpansion ? "visible" : "hidden",
        transition: disableExpansion
          ? "none"
          : `max-height ${transitionDuration} cubic-bezier(0.4, 0, 0.2, 1)`,
        maxHeight: disableExpansion
          ? "none"
          : isOpen
          ? showAllIngredients
            ? "none"
            : `${contentHeight}px`
          : "0px",
        padding: isPreviewPanel ? "0" : "0 0.75em",
        backgroundColor: "transparent",
        willChange: disableExpansion ? "auto" : "max-height",
        contain: disableExpansion ? "none" : "layout",
        ...(isPreviewPanel ? {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        } : {}),
      }}
    >
      {isOpen && (isPreviewPanel ? previewPanelContent : standardExpandedContent)}
    </div>
  );

  return (
    <RecipeCard
      recipe={recipe}
      index={index}
      isSelected={isSelected}
      isOpen={isOpen}
      onClick={handleTap}
      swipeHandlers={handlers}
      showActions={showActions}
      cardMovingOver={cardMovingOver}
      quickActions={quickActionsComponent}
      hintOverlay={hintOverlayComponent}
      swipeIcon={swipeIconComponent}
      source={{ name: recipe.books.name, type: "book", isTrusted: false }}
      disableHover={expanded}
      onImageLoad={updateContentHeight}
      onViewOriginal={isPreviewPanel ? handleViewOriginalRecipe : undefined}
    >
      {expandedContent}
    </RecipeCard>
  );
}

export default RecipeDetail;
